import * as React from 'react';
import { isPartialDate, partialDateFromDate, partialDateToValue, sortPartialDate } from 'neuro-utils';
import TileContentMaker from 'Components/DashboardTile/TileContentMaker';
import { useAppSelector as useSelector } from 'Store/index';
import { localizeCodenames } from '../utils';
import { useIntl } from 'react-intl';
import { examination } from 'neuro-schemas';
import { formatPartialDate } from 'Utility/date';
import { filterDocumentsByType } from 'Utility/documentHandling';

const { examinationsList, smaLabResultTypes, srPriorityExams } = examination.config;

const ExaminationDash = ({ documents }: IOwnProps): React.JSX.Element => {
  const platform = useSelector((s: { session: ISessionStore }) => s.session.platforms?.selected);

  /** Filter documents if platform is SMA or DMD */
  const docs =
    platform && ['sma', 'dmd'].includes(platform)
      ? filterDocumentsByType(documents, ['examination']).filter(
          d => typeof d.codeName === 'string' && smaLabResultTypes.includes(d.codeName),
        )
      : documents;

  const sortedArr = docs.sort(
    (n1, n2) => (sortPartialDate(n2.date, n1.date) || n1.codeName?.localeCompare(n2.codeName ?? 'z')) ?? 0,
  );

  const twoYearsAgo = new Date(new Date().setFullYear(new Date().getFullYear() - 2));
  const twoYearsAgoPartial = partialDateFromDate(twoYearsAgo);

  const srPriorities = filterDocumentsByType(documents, ['examination'])
    .filter(
      d =>
        srPriorityExams.includes(d.codeName ?? '') &&
        d.date &&
        partialDateToValue(d.date) > partialDateToValue(twoYearsAgoPartial),
    )
    .sort((n1, n2) => (sortPartialDate(n2.date, n1.date) || n1.codeName?.localeCompare(n2.codeName ?? 'z')) ?? 0);

  const srAbnormalPriorities = srPriorities.filter(
    d =>
      d.abnormalFlags &&
      !docs.find(doc => partialDateToValue(doc.date) > partialDateToValue(d.date) && doc.code === d.code),
  );

  const { formatMessage } = useIntl();
  const fm = (id: string) => formatMessage({ id });

  if (platform === 'sleepApnea' && srAbnormalPriorities.length > 0) {
    const subTitle = fm('examination.abnormalPriorities');
    return (
      <TileContentMaker
        type="listWithSubheaders"
        data={{
          [subTitle]: {
            type: 'recentList',
            data: srAbnormalPriorities.map((exam, i, arr) => {
              if (i > 3 && arr.length > 5) {
                return {
                  title: `+${arr.length - 4} ${fm('examination.otherExams')}`,
                  value: ' ',
                  key: exam._id,
                };
              }
              return {
                date: isPartialDate(exam.date) ? formatPartialDate(exam.date) : '',
                title: exam.codeName ? localizeCodenames(exam.codeName, fm) : '',
                value: exam.value && `${exam.value}${exam.units ? ' ' + exam.units : ''}`,
                key: exam._id,
              };
            }),
          },
        }}
      />
    );
  }

  return platform === 'mgravis' ? (
    <TileContentMaker
      type="specificList"
      data={
        // 'S-AChR-Ab', 'S-MuSKAb', 'S-TitiAb', 'LRP4_antibody', 'ryanodine_antibody', 'S-CaKPQAb'
        ['3427', '6101', '6710', 'LRP4_antibody', 'ryanodine_antibody', '6635'].map(code => {
          const doc = sortedArr.find(d => d.code === code);
          return {
            date: doc && doc.date ? formatPartialDate(doc.date) : fm('examination.notDone'),
            key: code,
            title: localizeCodenames(examinationsList.find(exam => exam.code === code)?.codeName ?? '', fm),
            value: doc?.value && `${doc.value}${doc.units ? ` ${doc.units}` : ''}`,
          };
        })
      }
    />
  ) : (
    <TileContentMaker
      type="recentList"
      data={sortedArr.map((d, i) => ({
        date: isPartialDate(d.date) ? formatPartialDate(d.date) : '',
        title: d.codeName ? localizeCodenames(d.codeName, fm) : '',
        value: d.value && `${d.value}${d.units ? ' ' + d.units : ''}`,
        key: `${i}`,
      }))}
    />
  );
};

interface IOwnProps {
  documents: Array<IDocumentWithTypeV2<'examination'>>;
}

export default ExaminationDash;
